import { useSite } from '../context/SiteContext';
import { pathFor } from '../i18n/routes';
import useSEO from '../hooks/useSEO';
import { MaskedLines, Kicker, Reveal } from '../components/Reveal';
import { CTABanner } from '../components/WorkGrid';

const FOUNDER_IMGS = ['/assets/founder-miguel.webp', '/assets/founder-rafael.webp'];

export default function About() {
  const { lang, t } = useSite();
  useSEO(t.meta.about, 'about', lang);

  return (
    <div data-testid="about-page">
      <section className="relative flex min-h-[85svh] items-end overflow-hidden">
        <img
          src="/assets/about-hero.webp"
          alt=""
          aria-hidden="true"
          style={{ objectPosition: 'center 40%' }}
          className="absolute inset-0 h-full w-full object-cover opacity-40"
          data-testid="about-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/70" />
        <div className="relative z-10 mx-auto w-full max-w-[1600px] px-5 pb-20 pt-48 sm:px-8">
          <Kicker>{t.about.kicker}</Kicker>
          <h1 className="mt-6 font-heading text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
            <MaskedLines
              delay={0.1}
              lines={[
                t.about.titleA,
                t.about.titleB,
                <span key="c" className="brand-text">{t.about.titleC}</span>,
              ]}
            />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-300 sm:text-lg">{t.about.text}</p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-zinc-900" data-testid="about-founders">
        <div className="mx-auto max-w-[1600px] px-5 py-24 sm:px-8 sm:py-32">
          <Reveal className="mb-12">
            <Kicker>{t.about.foundersKicker}</Kicker>
            <h2 className="mt-4 font-heading text-4xl font-extrabold uppercase tracking-tight sm:text-5xl">
              {t.about.foundersTitle}
            </h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2">
            {t.about.founders.map((f, i) => (
              <Reveal key={f.name} delay={i * 0.15}>
                <div className="group" data-testid={`founder-${i}`}>
                  <div className="image-frame aspect-[4/5] sm:aspect-[4/4]">
                    <img
                      src={FOUNDER_IMGS[i]}
                      alt={f.name}
                      loading="lazy"
                      style={{ objectPosition: 'center top' }}
                      className="transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 p-6 sm:p-8">
                      <p className="font-heading text-2xl font-extrabold uppercase tracking-tight sm:text-3xl">{f.name}</p>
                      <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.25em] brand-text">{f.role}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        title={t.contact.title}
        cta={t.about.cta}
        to={pathFor('contact', lang)}
        testId="about-cta"
      />
    </div>
  );
}
